interface BackoffOptions {
  fixed?: number
  base?: number
  max?: number
  jitter?: boolean
}

type DelayCalcFn = (attemptNum: number) => number

interface RetryOptions {
  retries?: number
  delay?: number | DelayCalcFn
  shouldRetry?: (error: unknown, attemptNum: number) => boolean
}

const waitWithPromise = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(() => resolve(), ms)
  })

const toError = (error: unknown): Error => {
  if (error instanceof Error) {
    return error
  }

  return new Error(String(error))
}

export const backoffDelay = (
  attemptNum: number,
  { fixed = 0, base = 250, max = 10_000, jitter = true }: BackoffOptions = {}
) => {
  const exp = base * 2 ** attemptNum // 250ms, 500ms, 1s, 2s, ...
  const jittered = jitter ? Math.floor(Math.random() * exp) : exp
  return Math.min(max, fixed + jittered)
}

export const makeBackoffDelay = (args: BackoffOptions = {}): DelayCalcFn => {
  return (attemptNum: number) => {
    return backoffDelay(attemptNum, args)
  }
}

export const retryWithDelay = async <T>(
  fn: () => T,
  { retries = 10, delay = 1000, shouldRetry }: RetryOptions = {},
  attemptNum = 0
): Promise<T> => {
  try {
    return await fn()
  } catch (error) {
    if (shouldRetry && !shouldRetry(error, attemptNum)) {
      return Promise.reject(toError(error))
    }

    if (attemptNum > retries) {
      return Promise.reject(
        new Error(`${retries} retries failed with: ${toError(error).message}`)
      )
    }

    const interval = delay instanceof Function ? delay(attemptNum) : delay
    await waitWithPromise(interval)

    return retryWithDelay(
      fn,
      { retries: retries, delay, shouldRetry },
      attemptNum + 1
    )
  }
}
